// hooks
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSaveCla } from './ClaMaintenanceForm.hooks';

// utils
import buildSchema from '../../ClaMaintenance.schema';
import { externalCodeToSave } from '../../ClaMaintenance.externalCode';
import { yupResolver } from '@hookform/resolvers/yup';

// components
import { LoadingButton } from '@mui/lab';
import { Box, styled } from '@mui/material';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import {
  ControlledTextField,
  ControlledCheckbox
} from 'components/shared/ControlledFields';
import { ClaMaintenanceFormProps } from './ClaMaintenanceForm.types';
import { ClaRequest } from 'generated/index';

type ClaMaintenanceFormData = Partial<ClaRequest>;

const SpacedBox = styled(Box)(({ theme }) => ({
  gap: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  paddingTop: theme.spacing(4)
}));

export const ClaMaintenanceForm = ({
  rowData,
  handleClose,
  rows
}: ClaMaintenanceFormProps) => {
  const { t } = useTranslation('common');

  const methods = useForm<ClaMaintenanceFormData>({
    defaultValues: {
      claId: rowData?.claId,
      code: rowData?.claCode ?? '',
      name: rowData?.name ?? '',
      externalCode: rowData?.claExt ?? '',
      isDefault: rowData?.isDefault ?? false
    },
    resolver: yupResolver(buildSchema(rows, rowData?.claId, rowData?.claExt))
  });

  const { mutate, isLoading } = useSaveCla({
    onSuccess: handleClose
  });

  const { control, handleSubmit } = methods;

  const onSubmit = (data: ClaMaintenanceFormData) => {
    mutate({
      ...data,
      externalCode: externalCodeToSave(data.externalCode, rowData?.claExt)
    });
  };

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <SpacedBox>
        <ControlledTextField
          name="code"
          control={control}
          variant="outlined"
          label={t('adminPortal.claMaintenance.code')}
          required
        />
        <ControlledTextField
          name="name"
          control={control}
          variant="outlined"
          label={t('adminPortal.claMaintenance.name')}
          required
        />
        <ControlledTextField
          name="externalCode"
          control={control}
          variant="outlined"
          label={t('adminPortal.claMaintenance.externalCode')}
        />
        <ControlledCheckbox
          name="isDefault"
          control={control}
          label={t('adminPortal.claMaintenance.importDefault')}
        />
      </SpacedBox>
      <MultiStepForm.Actions>
        <LoadingButton
          loading={isLoading}
          type="submit"
          variant="contained"
          autoFocus
          color="primary"
          fullWidth
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
